import React, { useEffect } from 'react';
import { Button } from 'antd';
import { ArrowLeftOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import logoHandBtn from '../assets/logo_hand_button.png';

interface PrivacyPolicyViewProps {
  variant?: 'public' | 'app';
  onBack?: () => void;
}

const SECTIONS = [
  {
    title: '1. Information We Collect',
    body: 'Praxys.ai may collect account details such as name, email address, profile image, authentication identifiers, workspace settings, task prompts, files you upload, knowledge-base entries, agent configurations, and connector metadata needed to operate the service.',
  },
  {
    title: '2. Google User Data',
    body: 'When you connect Google services, Praxys.ai requests only the scopes needed for the selected connector. Google Drive access is used to create and manage Praxys.ai files and folders. Google Calendar access is used to read calendars and create, update, or manage calendar events when you ask agents or tools to do so.',
  },
  {
    title: '3. How We Use Information',
    body: 'We use information to authenticate users, run requested tasks, power agents and knowledge bases, sync connected services, provide support, improve reliability, prevent abuse, and maintain security and audit records.',
  },
  {
    title: '4. OAuth Tokens and Connector Credentials',
    body: 'Connector credentials such as OAuth tokens, webhook URLs, and access tokens are stored to provide the integration you enabled. Access is limited to operating, testing, refreshing, and disconnecting those connectors.',
  },
  {
    title: '5. Sharing and Disclosure',
    body: 'We do not sell personal information. We may share information with service providers that help operate Praxys.ai, with connected third-party services you authorize, when required by law, or to protect Praxys.ai, users, and the public.',
  },
  {
    title: '6. AI Processing',
    body: 'Prompts, files, knowledge-base content, and task context may be processed by AI models and tool services to produce requested outputs. Do not provide sensitive information unless it is necessary for your task and permitted by your organization.',
  },
  {
    title: '7. Data Retention',
    body: 'We retain account, task, connector, audit, and workspace data for as long as needed to provide the service, comply with obligations, resolve disputes, enforce agreements, and maintain security. You may request deletion where applicable.',
  },
  {
    title: '8. Security',
    body: 'We use reasonable technical and organizational safeguards to protect data, including credential encryption where supported. No system can be guaranteed completely secure.',
  },
  {
    title: '9. Your Choices',
    body: 'You may disconnect connectors, delete content, sign out, or request account/data assistance. You can also revoke Google access from your Google Account security settings.',
  },
  {
    title: '10. Children',
    body: 'Praxys.ai is not intended for children under 13 or anyone not permitted to use the service under applicable law.',
  },
  {
    title: '11. Changes to This Policy',
    body: 'We may update this Privacy Policy from time to time. Continued use of Praxys.ai after changes become effective means you acknowledge the updated policy.',
  },
];

const PrivacyPolicyView: React.FC<PrivacyPolicyViewProps> = ({ variant = 'app', onBack }) => {
  useEffect(() => {
    document.title = 'Privacy Policy | Praxys.ai — makes better';
  }, []);

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    window.history.replaceState({}, '', '/app');
    window.location.assign('/app');
  };

  const content = (
    <div className="praxys-view-panel praxys-terms-view">
      {variant === 'app' && (
        <Button
          icon={<ArrowLeftOutlined />}
          className="praxys-agent-pill-btn praxys-agent-pill-btn--accent praxys-legal-back-btn"
          onClick={handleBack}
          style={{ alignSelf: 'flex-start', width: 'auto', marginBottom: 16 }}
        >
          Back
        </Button>
      )}
      <div className="praxys-view-header">
        <SafetyCertificateOutlined className="praxys-view-icon" />
        <h2 className="praxys-view-title">Privacy Policy</h2>
        <span className="praxys-view-subtitle">Last updated June 23, 2026</span>
      </div>

      <div className="praxys-terms-card">
        <p className="praxys-terms-intro">
          This Privacy Policy explains how Praxys.ai collects, uses, and protects information, including data accessed through Google connectors.
        </p>

        <div className="praxys-terms-section-list">
          {SECTIONS.map(section => (
            <section key={section.title} className="praxys-terms-section">
              <h3>{section.title}</h3>
              <p>{section.body}</p>
            </section>
          ))}
        </div>

        <section className="praxys-terms-contact">
          <h3>Contact</h3>
          <p>
            Privacy questions or requests can be sent to <a href="mailto:support@praxys.ai">support@praxys.ai</a>.
          </p>
        </section>
      </div>
    </div>
  );

  if (variant === 'public') {
    return (
      <main className="praxys-public-home praxys-public-legal-page">
        <nav className="praxys-public-nav" aria-label="Public navigation">
          <a href="/" className="praxys-public-brand">
            <img src={logoHandBtn} alt="Praxys.ai" />
            <span>Praxys.ai</span>
            <span className="praxys-public-brand-tagline">makes better</span>
          </a>
          <div className="praxys-public-nav-links">
            <a href="/privacy">Privacy Policy</a>
            <a href="/terms">Terms of Service</a>
            <a href="/login" className="praxys-public-login-link">Login</a>
          </div>
        </nav>
        {content}
      </main>
    );
  }

  return content;
};

export default PrivacyPolicyView;
